import { useState, useEffect, useMemo, Fragment } from "react";
import { ArrowUpRight, Weight, Zap, ShieldCheck, Wind } from "lucide-react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ROOF_HERO_IMAGES, CONSTRUCTION_IMAGES } from "@/data/roofracks";
import { SEO } from "@/components/SEO";
import { HoverImage } from "@/components/HoverImage";

const API = process.env.REACT_APP_BACKEND_URL;
const K9_RACK_VEHICLES = [
  { key: "toyota", label: "Toyota", subtitle: "Hilux · Land Cruiser · Prado · Fortuner", image: "/products/roofracks/731077_276c5817539f4602b6810baa9712dcb4~mv2.jpg" },
  { key: "mitsubishi", label: "Mitsubishi", subtitle: "Pajero · Triton", image: "/products/roofracks/731077_74bf526e503b4a47ab96c073e98519b9~mv2.jpg" },
  { key: "landrover", label: "Land Rover", subtitle: "Defender · Discovery", image: "/products/roofracks/731077_a659dcb6dd0645f4b9e4387b8f2e2ae1~mv2.jpg" },
  { key: "ford", label: "Ford", subtitle: "Ranger · Everest", image: "/products/roofracks/731077_8cab8fa0483946ceac1ad35b8758fb33~mv2.jpg" },
  { key: "suzuki", label: "Suzuki", subtitle: "Jimny", image: "/products/roofracks/731077_96231b93e6fb4472a9fd2fb3e74100b9~mv2.jpg" },
  { key: "ineos", label: "INEOS Grenadier", subtitle: "Purpose-built expedition kit", image: "/products/roofracks/731077_89a57dbab4704dd3b0cff4234ef86bba~mv2.jpeg" },
  { key: "gwm", label: "GWM", subtitle: "Tank · Cannon", image: "/products/roofracks/731077_33462357c8914137ab2bb9cfb397cdad~mv2.jpg" },
];

const fmt = (n) => n?.toLocaleString("en-NZ", { minimumFractionDigits: n % 1 !== 0 ? 2 : 0 });

// These "mount" items live full-time on their own category page instead — the 2
// ensuite brackets are already cross-linked from Ensuites' ENSUITE_LINKED_PRODUCTS,
// and the wind deflector is now cross-linked from the Roof Tents page. Keeping them
// out of this generic Mounts grid avoids showing ensuite/roof-tent gear to someone
// browsing specifically for roof rack parts.
const ROOFRACK_MOUNTS_MOVED_ELSEWHERE = ["ensuite-to-load-bar-brackets", "ensuite-to-roof-tent-brackets", "quick-pitch-roof-tent-wind-deflector"];

// Already native elsewhere on the site — cross-linked here, not duplicated
// (same pattern as WELLSIDE_ACCESSORY_LINKED_PRODUCTS in WellsideAccessoriesPage.jsx).
// Keyed by K9_RACK_VEHICLES key so each linked rack surfaces on its matching
// vehicle tab in the "K9 Platform Racks By Vehicle" grid below.
const ROOFRACK_LINKED_PRODUCTS_BY_VEHICLE = {
  toyota: [
    { name: "Bush Cabin Cab Rack Kit for Hilux", slug: "bushcabin-hilux-cab-rack-kit", variants: "Hilux · Bush Cabin Cab Space", price: 1399, thumb: "/api/files/xpeditionz/bushcabinaccessory/bushcabin-hilux-cab-rack-kit/6307bccf-1b6f-4121-9c4d-02404186ec49.webp", to: "/bush-cabin-accessories/bushcabin-hilux-cab-rack-kit" },
  ],
};

const RoofRackCard = ({ p, index }) => (
  <Link
    to={p.to || `/roof-racks/${p.slug}`}
    data-testid={`rr-product-${p.slug}`}
    className="group relative block overflow-hidden border border-[var(--border-default)] hover:border-[var(--brand-primary)] transition-colors bg-[var(--bg-secondary)]"
  >
    <div className="aspect-[4/3] bg-[var(--bg-tertiary)] overflow-hidden relative">
      <HoverImage primary={p.thumb} secondary={p.gallery?.[1]?.src} alt={p.name} />
    </div>
    <div className="p-5 flex flex-col gap-3">
      <div>
        <h3 className="font-display text-lg tracking-wide text-white leading-tight">{p.name}</h3>
        <p className="text-xs text-[var(--text-tertiary)] mt-1 font-mono uppercase tracking-wider">{p.variants}</p>
      </div>
      <div className="flex items-end justify-between pt-3 border-t border-[var(--border-default)]">
        <div>
          {p.price != null ? (
            <>
              <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-[var(--text-tertiary)]">{p.priceFrom ? "From" : ""}</p>
              <p className="font-display text-xl text-[var(--brand-primary)] leading-none mt-1">${fmt(p.price)}</p>
            </>
          ) : (
            <p className="font-display text-lg text-[var(--brand-primary)] leading-none mt-1">{p.availability || "Enquire"}</p>
          )}
        </div>
        <span className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-[0.15em] text-white/70 group-hover:text-[var(--brand-primary)] transition-colors">
          View <ArrowUpRight size={14} />
        </span>
      </div>
    </div>
  </Link>
);

const K9_FEATURES = [
  { icon: ShieldCheck, title: "Fully Welded Sub-Structure", desc: "Robust all-alloy welded frame beneath the deck. Many platform racks — including Front Runner — have no separate sub-structure at all." },
  { icon: Weight, title: "Double the Mounting Points", desc: "Up to 2× the roof mounting points of mainstream alloy platforms. Superior load distribution, less roof stress." },
  { icon: Wind, title: "35mm Ultra-Low Profile", desc: "Alloy wind deflector included. Whisper quiet at highway speeds. Ideal for SUV fitment." },
  { icon: Zap, title: "No Plastic. No Rivets.", desc: "All-alloy corners, fully welded. No structural plastic or fasteners in the load path. Built to survive real off-road abuse." },
];

const SUB_NAV = [
  { id: "k9", label: "K9 Racks" },
  { id: "loadbars", label: "Load Bars" },
  { id: "mounts", label: "Awning & Tent Mounts" },
  { id: "accessories", label: "Accessories" },
];

const SectionHeader = ({ over, title, kicker, sub }) => {
  const titleLines = Array.isArray(title) ? title : [title];
  return (
    <div className="flex items-end justify-between flex-wrap gap-6 mb-12">
      <div>
        <p className="overline mb-4">{over}</p>
        <h2 className="font-display text-4xl md:text-6xl text-white leading-[0.95]">
          {titleLines.map((line, i) => (
            <Fragment key={line}>
              {line}
              {i < titleLines.length - 1 && <br />}
            </Fragment>
          ))}
        </h2>
        {kicker && <p className="text-[var(--brand-primary)] font-mono text-xs uppercase tracking-[0.2em] mt-4">{kicker}</p>}
      </div>
      {sub && <p className="text-[var(--text-secondary)] max-w-md font-mono text-sm">{sub}</p>}
    </div>
  );
};

export default function RoofRacksPage() {
  const [products, setProducts] = useState(null); // null = still loading
  const [activeVehicle, setActiveVehicle] = useState(K9_RACK_VEHICLES[0].key);

  useEffect(() => {
    axios.get(`${API}/api/products/roofrack`).then((res) => setProducts(res.data)).catch((err) => { console.error(err); setProducts([]); });
  }, []);

  const loadbarProducts = useMemo(() => (products || []).filter((p) => p.type === "loadbar"), [products]);
  const mountProducts = useMemo(
    () => (products || []).filter((p) => p.type === "mount" && !ROOFRACK_MOUNTS_MOVED_ELSEWHERE.includes(p.slug)),
    [products]
  );
  const accessoryProducts = useMemo(() => (products || []).filter((p) => p.type === "accessory"), [products]);
  const platformRacks = useMemo(() => {
    const native = (products || []).filter((p) => p.vehicle === activeVehicle || (p.vehicles && p.vehicles.includes(activeVehicle)));
    const linked = ROOFRACK_LINKED_PRODUCTS_BY_VEHICLE[activeVehicle] || [];
    return [...native, ...linked];
  }, [products, activeVehicle]);

  if (products === null) {
    return <div className="min-h-[60vh] flex items-center justify-center text-white/50 font-mono text-sm">Loading…</div>;
  }

  return (
    <div data-testid="roofracks-page">
      <SEO
        path="/roof-racks"
        title="EEZI-AWN K9 Roof Racks NZ"
        description="World-expedition-grade K9 platform roof racks for Toyota, Ford, Land Rover, Mitsubishi, Suzuki, INEOS, GWM and more. Load bars, awning brackets, roof tent mounts and accessories. Full ecosystem."
        image={ROOF_HERO_IMAGES.hero}
      />
      {/* HERO */}
      <section className="relative min-h-[100svh] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img src={ROOF_HERO_IMAGES.hero} alt="EEZI-AWN K9 roof rack expedition" className="w-full h-full object-cover" style={{objectPosition: '60% center'}} />
          <div className="absolute inset-0 bg-gradient-to-t from-[var(--bg-main)] from-15% via-black/25 via-50% to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/10 to-transparent md:via-transparent" />
        </div>
        <div className="relative z-10 container-x pb-20 pt-32 w-full">
          <p className="overline mb-6">— EEZI-AWN K9 · Roof Racks · Load Bars · Mounts</p>
          <h1 className="font-display text-[12vw] md:text-[9vw] lg:text-[7.5vw] leading-[0.85] text-white hero-text-shadow max-w-[14ch]">
            THE ONLY TRUE<br />
            <span className="text-[var(--brand-primary)]">EXPEDITION-GRADE</span><br />
            ROOF RACK IN NZ.
          </h1>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-10 items-end">
            <p className="text-base md:text-lg font-medium md:font-normal text-white/95 md:text-white/80 leading-relaxed max-w-xl [text-shadow:0_1px_3px_rgba(0,0,0,0.9),0_2px_20px_rgba(0,0,0,0.7)] md:[text-shadow:none]">
              For serious 4WD use, K9 racks are engineered to a completely different standard. Fully welded sub-structure. No plastic. No rivets. Expedition-proven on every major continent — without a price premium over mainstream systems.
            </p>
            <div className="flex flex-wrap gap-3 md:justify-end">
              <a href="#k9" data-testid="rr-cta-k9" className="btn-primary">Find Your Rack <ArrowUpRight size={14} /></a>
              <a href="#loadbars" className="btn-secondary">Load Bars</a>
            </div>
          </div>
        </div>
      </section>

      {/* SUB NAV */}
      <section className="sticky top-20 z-40 glass-header border-b border-white/5">
        <div className="container-x flex overflow-x-auto whitespace-nowrap gap-1 py-3">
          {SUB_NAV.map((s) => (
            <a
              key={s.id}
              href={`#${s.id}`}
              data-testid={`rr-subnav-${s.id}`}
              className="px-4 py-2 font-mono text-xs tracking-[0.15em] uppercase text-white/70 hover:text-[var(--brand-primary)] transition-colors"
            >
              {s.label}
            </a>
          ))}
        </div>
      </section>

      {/* K9 WHY */}
      <section className="container-x py-24 md:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <p className="overline mb-6">— A Different Standard</p>
            <h2 className="font-display text-4xl md:text-5xl leading-[0.95] text-white">
              WHERE MAINSTREAM<br />
              RACKS <span className="text-[var(--brand-primary)]">STOP.</span><br />
              K9 IS JUST GETTING STARTED.
            </h2>
          </div>
          <div className="lg:col-span-7 space-y-5 text-[var(--text-secondary)] text-lg leading-relaxed">
            <p>
              A common mistake we see: customers upgrading from one mainstream touring rack to another — stepping sideways, or sometimes even backwards. Brands like Yakima, Rhino-Rack, Front Runner and ARB make excellent racks for general touring, but in our view none is comparable to EEZI-AWN's K9 platform once a vehicle is built for serious 4WD use — particularly carrying expedition-grade roof tents or large freestanding awnings that place torsional loads through a rack. <span className="text-white">The limitations of mainstream systems begin to show.</span>
            </p>
            <p>
              For those seeking the ultimate upgrade, the EEZI-AWN K9 stands apart. The XpeditioNZ team has experience with long-term, cross-continent 4WD expeditions all around the world — and the K9 is now the only platform rack we'd trust on that kind of serious expedition. We believe they'll also simply hold up better to serious 4wding here at home too.
            </p>
            <p className="text-white italic border-l-2 border-[var(--brand-primary)] pl-4">
              "All up, the K9 is the most versatile, practical and rugged rack I've ever seen or used. It's hard to pick a fault — I'd give it a 99/100 score."
              <span className="block text-xs font-mono uppercase tracking-[0.2em] text-[var(--text-tertiary)] mt-3">— 4x4 Australia · Product Test</span>
            </p>
          </div>
        </div>
      </section>

      {/* CONSTRUCTION DETAIL — structural images */}
      <section className="container-x pb-24">
        <div className="mb-12">
          <p className="overline mb-4">— Look Underneath</p>
          <h2 className="font-display text-4xl md:text-6xl text-white leading-[0.95] max-w-4xl">
            THE DETAIL YOU CAN'T SEE<br />
            <span className="text-[var(--brand-primary)]">FROM ABOVE.</span>
          </h2>
          <p className="text-[var(--text-secondary)] mt-6 max-w-2xl leading-relaxed text-lg">
            The difference between K9 and every mainstream platform rack lives in the construction details. Fully welded. Zero plastic in the load path. We flipped a rack upside down so you can see exactly what's holding it together — this is what genuine expedition engineering looks like.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-[var(--border-default)] border border-[var(--border-default)]">
          {CONSTRUCTION_IMAGES.map((c, i) => (
            <figure key={c.title} data-testid={`rr-construction-${i}`} className="bg-[var(--bg-secondary)] group">
              <div className="relative aspect-[4/3] overflow-hidden">
                <img src={c.src} alt={c.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.03]" />
                <span className="absolute top-4 left-4 font-mono text-[10px] tracking-[0.3em] uppercase text-[var(--brand-primary)] bg-black/60 backdrop-blur-sm px-3 py-1.5">{c.label}</span>
              </div>
              <figcaption className="p-8">
                <h3 className="font-display text-2xl md:text-3xl text-white leading-tight">{c.title}</h3>
                <p className="text-[var(--text-secondary)] mt-3 leading-relaxed">{c.caption}</p>
              </figcaption>
            </figure>
          ))}
        </div>
      </section>

      {/* K9 FEATURES BENTO */}
      <section className="container-x pb-24">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-[var(--border-default)] border border-[var(--border-default)]">
          {K9_FEATURES.map((f, i) => {
            const Icon = f.icon;
            return (
              <div key={f.title} data-testid={`rr-feature-${i}`} className="bg-[var(--bg-main)] p-8 hover:bg-[var(--bg-secondary)] transition-colors">
                <Icon size={26} className="text-[var(--brand-primary)] mb-6" strokeWidth={1.3} />
                <h3 className="font-display text-xl text-white tracking-wide leading-tight">{f.title}</h3>
                <p className="text-[var(--text-secondary)] mt-3 text-sm leading-relaxed">{f.desc}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* K9 RACKS BY VEHICLE — native listing */}
      <section id="k9" className="container-x pb-24 scroll-mt-32">
        <SectionHeader
          over="— Find Your K9 Rack"
          title={["K9 PLATFORM RACKS", "BY VEHICLE."]}
          sub="Can't see your vehicle? Get in touch and we'll assemble a custom kit."
        />
        <div className="flex overflow-x-auto whitespace-nowrap gap-2 mb-10 pb-1">
          {K9_RACK_VEHICLES.map((v) => (
            <button
              key={v.key}
              onClick={() => setActiveVehicle(v.key)}
              data-testid={`rr-vehicle-tab-${v.key}`}
              className={`px-5 py-3 font-mono text-xs tracking-[0.15em] uppercase transition-colors border ${
                activeVehicle === v.key
                  ? "bg-[var(--brand-primary)] text-black border-[var(--brand-primary)]"
                  : "border-[var(--border-default)] text-white/70 hover:text-white hover:border-white/40"
              }`}
            >
              {v.label}
            </button>
          ))}
        </div>
        {platformRacks.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {platformRacks.map((p, i) => <RoofRackCard key={p.slug} p={p} index={i} />)}
          </div>
        ) : (
          <p className="text-[var(--text-secondary)]" data-testid="rr-vehicle-empty">
            No racks listed yet for this vehicle — <a href="#enquire" className="text-[var(--brand-primary)] underline">get in touch</a> and we'll sort a custom kit.
          </p>
        )}
      </section>

      {/* LOAD BARS */}
      <section className="relative overflow-hidden">
        <div className="relative h-[45vh] md:h-[55vh]">
          <img src={ROOF_HERO_IMAGES.loadbars} alt="Load bars" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[var(--bg-main)] via-black/40 to-black/30" />
          <div className="absolute bottom-0 left-0 right-0 container-x pb-12">
            <p className="overline mb-3">— K9 Load Bars</p>
            <h2 className="font-display text-4xl md:text-6xl text-white leading-[0.95] max-w-3xl hero-text-shadow">
              LIGHTER FOOTPRINT.<br />
              <span className="text-[var(--brand-primary)]">SAME K9 DNA.</span>
            </h2>
          </div>
        </div>
      </section>

      <section id="loadbars" className="container-x py-24 scroll-mt-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-16">
          <div className="lg:col-span-5">
            <h3 className="font-display text-3xl md:text-4xl text-white leading-tight">
              WHEN YOU DON'T<br />NEED A FULL<br />PLATFORM RACK.
            </h3>
          </div>
          <div className="lg:col-span-7 space-y-4 text-[var(--text-secondary)] text-lg leading-relaxed">
            <p>
              All-aluminium and powder-coated stainless steel construction. Robust and long-lasting. <span className="text-white">No plastic anywhere in the load path.</span>
            </p>
            <p>
              Available from 1250mm to 1650mm. Choose <span className="text-white">TRACK</span>, <span className="text-white">GUTTER</span> or <span className="text-white">CLAMP</span> mount depending on your vehicle. All legs metal construction. Rear profile can be drilled and bolted — ideal for mounting all roof tents.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {loadbarProducts.map((p, i) => <RoofRackCard key={p.slug} p={p} index={i} />)}
        </div>
      </section>

      {/* MOUNTS */}
      <section id="mounts" className="container-x py-24 scroll-mt-32 border-t border-[var(--border-default)]">
        <SectionHeader
          over="— Awning & Roof Tent Mounts"
          title={["EVERYTHING YOU NEED", "TO MOUNT UP."]}
          sub="Brackets for awnings and roof tents — engineered to lock cleanly into K9 racks and load bars. Ensuite mounts live on the Ensuites page; the LED light kit and wind deflector live on their own product pages."
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {products.filter((p) => p.type === "mount" && !ROOFRACK_MOUNTS_MOVED_ELSEWHERE.includes(p.slug)).map((p, i) => <RoofRackCard key={p.slug} p={p} index={i} />)}
        </div>
      </section>

      {/* ACCESSORIES */}
      <section id="accessories" className="container-x pb-24 scroll-mt-32">
        <SectionHeader
          over="— Accessories"
          title={["THE FULL EEZI-AWN", "ECOSYSTEM."]}
          sub="Shovel mounts, Maxtrax brackets, Hi-Lift jack mounts, tables, showers, storage cubes and more. Everything mounts cleanly to your K9 rack."
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {accessoryProducts.map((p, i) => <RoofRackCard key={p.slug} p={p} index={i} />)}
        </div>
      </section>
    </div>
  );
}
